import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Switch, FormControlLabel, Box, Stack,
    Typography, IconButton, Select, MenuItem, FormControl,
    InputLabel, FormHelperText, Divider
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { RotationPattern, RotationPatternRequest } from '../../interfaces/shift/RotationPattern';
import { Shift } from '../../interfaces/shift/Shift';
import { RotationPatternService } from '../../services/rotation-pattern.service';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';

interface Props {
    open: boolean;
    onClose: () => void;
    institutionId: string;
    shifts: Shift[];
    pattern?: RotationPattern | null;
    onSaved: () => void;
}

interface FormState {
    name: string;
    daysPerStep: string;
    startDate: string;
    isActive: boolean;
}

interface FormErrors {
    name?: string;
    daysPerStep?: string;
    startDate?: string;
    steps?: string;
}

const emptyForm = (): FormState => ({
    name: '',
    daysPerStep: '7',
    startDate: '',
    isActive: true,
});

const RotationPatternEditDialog: React.FC<Props> = ({
    open, onClose, institutionId, shifts, onSaved,
    // pattern prop is accepted but edit mode is not yet implemented —
    // backend has no update endpoint. Kept for future compatibility.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    pattern,
}) => {
    const toast = useToast();
    const { t } = useTranslation(['shifts', 'common']);
    const [form, setForm] = useState<FormState>(emptyForm());
    const [errors, setErrors] = useState<FormErrors>({});
    const [steps, setSteps] = useState<Shift[]>([]);
    const [selectedShiftId, setSelectedShiftId] = useState<string>('');
    const [saving, setSaving] = useState(false);

    const activeShifts = shifts.filter(s => s.isActive);

    useEffect(() => {
        if (open) {
            setForm(emptyForm());
            setErrors({});
            setSteps([]);
            setSelectedShiftId(activeShifts.length > 0 ? activeShifts[0].id : '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
        setForm(prev => ({ ...prev, [key]: value }));
        setErrors(prev => ({ ...prev, [key]: undefined }));
    };

    const handleAddStep = () => {
        if (!selectedShiftId) return;
        const shift = shifts.find(s => s.id === selectedShiftId);
        if (!shift) return;
        setSteps(prev => [...prev, shift]);
        setErrors(prev => ({ ...prev, steps: undefined }));
    };

    const handleMoveUp = (index: number) => {
        if (index === 0) return;
        setSteps(prev => {
            const next = [...prev];
            [next[index - 1], next[index]] = [next[index], next[index - 1]];
            return next;
        });
    };

    const handleMoveDown = (index: number) => {
        setSteps(prev => {
            if (index === prev.length - 1) return prev;
            const next = [...prev];
            [next[index], next[index + 1]] = [next[index + 1], next[index]];
            return next;
        });
    };

    const handleRemoveStep = (index: number) => {
        setSteps(prev => prev.filter((_, i) => i !== index));
    };

    const validate = (): boolean => {
        const errs: FormErrors = {};

        if (!form.name.trim()) {
            errs.name = t('patternEdit.errors.nameRequired');
        } else if (form.name.trim().length > 80) {
            errs.name = t('patternEdit.errors.nameTooLong', { max: 80 });
        }

        const days = Number(form.daysPerStep);
        if (!form.daysPerStep || isNaN(days) || days < 1 || days > 90) {
            errs.daysPerStep = t('patternEdit.errors.daysRange', { min: 1, max: 90 });
        }

        if (!form.startDate) {
            errs.startDate = t('patternEdit.errors.startRequired');
        }

        if (steps.length === 0) {
            errs.steps = t('patternEdit.errors.stepsRequired');
        }

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        const body: RotationPatternRequest = {
            name: form.name.trim(),
            daysPerStep: Number(form.daysPerStep),
            startDate: form.startDate,
            isActive: form.isActive,
            shiftIdsInOrder: steps.map(s => s.id),
        };

        setSaving(true);
        try {
            await RotationPatternService.create(institutionId, body);
            toast.showSuccess(t('patternEdit.created'));
            onSaved();
            onClose();
        } catch (err) {
            toast.showApiError(err, t('patternEdit.saveFailed'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t('patternEdit.title')}</DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 1 }}>
                    <Stack spacing={2}>
                        <TextField
                            label={t('common:fields.name')}
                            fullWidth
                            value={form.name}
                            onChange={e => setField('name', e.target.value)}
                            error={!!errors.name}
                            helperText={errors.name}
                            required
                            inputProps={{ 'data-testid': 'pattern-name', maxLength: 80 }}
                        />

                        <TextField
                            label={t('patternEdit.daysPerStep')}
                            type="number"
                            fullWidth
                            value={form.daysPerStep}
                            onChange={e => setField('daysPerStep', e.target.value)}
                            error={!!errors.daysPerStep}
                            helperText={errors.daysPerStep || t('patternEdit.daysPerStepHelp')}
                            required
                            inputProps={{ min: 1, max: 90, 'data-testid': 'pattern-days-per-step' }}
                        />

                        <TextField
                            label={t('patternEdit.startDate')}
                            type="date"
                            fullWidth
                            value={form.startDate}
                            onChange={e => setField('startDate', e.target.value)}
                            error={!!errors.startDate}
                            helperText={errors.startDate}
                            required
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ 'data-testid': 'pattern-start-date' }}
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.isActive}
                                    onChange={e => setField('isActive', e.target.checked)}
                                    data-testid="pattern-active"
                                />
                            }
                            label={t('common:status.active')}
                        />

                        <Divider />

                        <Typography variant="subtitle2">{t('patternEdit.stepsHeading')}</Typography>

                        {steps.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                {t('patternEdit.noSteps')}
                            </Typography>
                        ) : (
                            <Stack spacing={1}>
                                {steps.map((step, index) => (
                                    <Box
                                        key={`${step.id}-${index}`}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            p: 1,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{ minWidth: 24, fontWeight: 'bold', color: 'text.secondary' }}
                                        >
                                            {index + 1}
                                        </Typography>
                                        <Typography variant="body2" sx={{ flex: 1 }}>
                                            {step.code} — {step.name}
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleMoveUp(index)}
                                            disabled={index === 0}
                                            aria-label={t('patternEdit.moveUp')}
                                        >
                                            <ArrowUpwardIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleMoveDown(index)}
                                            disabled={index === steps.length - 1}
                                            aria-label={t('patternEdit.moveDown')}
                                        >
                                            <ArrowDownwardIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleRemoveStep(index)}
                                            color="error"
                                            aria-label={t('patternEdit.removeStep')}
                                        >
                                            <RemoveCircleOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Stack>
                        )}

                        {errors.steps && (
                            <FormHelperText error>{errors.steps}</FormHelperText>
                        )}

                        {activeShifts.length > 0 ? (
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                <FormControl sx={{ flex: 1 }} size="small">
                                    <InputLabel id="add-step-label">{t('patternEdit.addStep')}</InputLabel>
                                    <Select
                                        labelId="add-step-label"
                                        label={t('patternEdit.addStep')}
                                        value={selectedShiftId}
                                        onChange={e => setSelectedShiftId(e.target.value as string)}
                                        inputProps={{ 'data-testid': 'add-step-select' }}
                                    >
                                        {activeShifts.map(s => (
                                            <MenuItem key={s.id} value={s.id}>
                                                {s.code} — {s.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Button
                                    variant="outlined"
                                    onClick={handleAddStep}
                                    disabled={!selectedShiftId}
                                    data-testid="add-step-button"
                                    sx={{ whiteSpace: 'nowrap', alignSelf: 'center' }}
                                >
                                    {t('common:actions.add')}
                                </Button>
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                {t('patternEdit.noActiveShifts')}
                            </Typography>
                        )}
                    </Stack>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>{t('common:actions.cancel')}</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={saving}>
                    {saving ? t('saving') : t('common:actions.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RotationPatternEditDialog;
